import React, { useState, useEffect } from "react";
import { Box, Chip, Tooltip, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReplyIcon from "@mui/icons-material/Reply";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

// Local imports
import ListingPageTemplate from "@Pages/ListingPageTemplate/ListingPageTemplate";
import themeCreator from "@Themes/themeCreator";
import axiosInstance from "@API/axios";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import { setSnackbar } from "@Redux/snackBarSlice";
import usePaginate from "@Hooks/usePaginate";
import { stringCustomOperators } from "@Utils/Validations/HelperFunctions/CustomFilters";

// ===================== CONSTANTS =====================
const INITIAL_PAGE_SIZE = 10;
const ERROR_FETCHING_DATA = "Error fetching support requests";
const DEFAULT_VALUE = "-";

// Status configuration
const STATUS_COLORS = {
  PENDING: "warning",
  RESPONDED: "success",
  REASSIGNED: "info",
  COMPLETED: "default",
  CANCELLED: "error",
};

const STATUS_ICONS = {
  PENDING: <ScheduleIcon fontSize="small" />,
  RESPONDED: <CheckCircleIcon fontSize="small" />,
  REASSIGNED: <SwapHorizIcon fontSize="small" />,
};

// ===================== HELPER FUNCTIONS =====================
const formatDate = (dateString) => {
  if (!dateString) return DEFAULT_VALUE;
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return dateString;
  }
};

const getNestedValue = (obj, path) => {
  const value = path.split(".").reduce((acc, part) => acc?.[part], obj);
  return value || DEFAULT_VALUE;
};

// ===================== MAIN COMPONENT =====================
function GenericSupportListing({
  pageTitle,
  apiRoute,
  showResponseAction = true,
  showViewAction = true,
  PageIcon,
  iconColor,
  buttons = [],
  extraComponents = null,
}) {
  // ===== Component State =====
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  // ===== Hooks =====
  const navigate = useNavigate();
  const theme = themeCreator();
  const dispatch = useDispatch();
  const { loading: paginateLoading, getPage } = usePaginate();

  // ===== Handlers =====
  const handleViewRequest = (requestId) => {
    navigate(`/support-request/${requestId}`);
  };

  // ===== Data Fetching =====
  const fetchPage = (queryOptions, filterModel) => {
    const filterItems = (filterModel?.items || []).map((item) => {
      const cleaned = { ...item };
      if (cleaned.value && typeof cleaned.value === "string") {
        cleaned.value = cleaned.value.trim();
      }
      return cleaned;
    }).filter((item) => item.field && item.operator);

    getPage(apiRoute, ERROR_FETCHING_DATA, queryOptions, filterItems).then(
      (data) => {
        if (data) {
          setRows(data.content || []);
          setRowCount(data.totalElements || 0);
        }
      }
    );
  };

  // ===== Column Configuration =====
  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.5,
      type: "number",
      sortable: false,
      filterable: false,
      renderCell: (params) => `#${params.row.id}`,
    },
    {
      field: "ingredientName",
      headerName: "Ingredient",
      flex: 1.2,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Box fontWeight="medium">{params.row.ingredientName || "N/A"}</Box>
          <Box fontSize="0.75rem" color="text.secondary">
            Exit Plan #{params.row.exitPlanId}
          </Box>
        </Box>
      ),
    },
    {
      field: "createdByEmail",
      headerName: "Requested By",
      flex: 1.5,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
      valueGetter: (params) => params.row.createdBy?.email || DEFAULT_VALUE,
      renderCell: (params) => (
        <Box>
          <Box>{params.row.createdBy?.username || "Unknown"}</Box>
          <Box fontSize="0.75rem" color="text.secondary">
            {params.row.createdBy?.email}
          </Box>
        </Box>
      ),
    },
    {
      field: "supportQuery",
      headerName: "Support Query",
      flex: 2,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.supportQuery || ""}>
          <Box
            sx={{
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.row.supportQuery || DEFAULT_VALUE}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      type: "singleSelect",
      valueOptions: ["PENDING", "RESPONDED", "REASSIGNED", "COMPLETED", "CANCELLED"],
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            icon={STATUS_ICONS[params.row.status]}
            label={params.row.status}
            color={STATUS_COLORS[params.row.status] || "default"}
            size="small"
          />
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      flex: 0.8,
      type: "date",
      sortable: false,
      filterable: false,
      valueGetter: (params) =>
        params.row.createdAt ? new Date(params.row.createdAt) : null,
      renderCell: (params) => formatDate(params.row.createdAt),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      minWidth: 120,
      maxWidth: 120,
      disableReorder: true,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          {showViewAction && (
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={() => handleViewRequest(params.row.id)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {showResponseAction &&
            (params.row.status === "PENDING" ||
              params.row.status === "REASSIGNED") && (
              <Tooltip title="Respond">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleViewRequest(params.row.id)}
                >
                  <ReplyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
        </Box>
      ),
    },
  ];

  // ===== Table Configuration =====
  const tableProps = {
    data: rows,
    columns: columns,
    disableRowSelectionOnClick: true,
    disableColumnMenu: true,
    initialColumnVisibilityModel: {
      id: true,
      ingredientName: true,
      createdByEmail: true,
      supportQuery: true,
      status: true,
      createdAt: true,
      actions: true,
    },
    rowCount,
    rows: rows || [],
    isLoading: paginateLoading,
    fetchPage,
    columnsEnable: true,
    filtersEnable: true,
    densityEnable: true,
    exportEnable: false,
    searchBarEnable: true,
    InitialPageSize: INITIAL_PAGE_SIZE,
  };

  // ===== Render =====
  return (
    <Box sx={styles.mainContainer}>
      <ListingPageTemplate
        title={pageTitle}
        PageIcon={PageIcon}
        iconColor={iconColor}
        buttons={buttons}
        tableProps={tableProps}
        serverPagination={true}
      />
      {extraComponents}
    </Box>
  );
}

// ===================== STYLES =====================
const styles = {
  mainContainer: {
    p: 0,
  },
};

export default GenericSupportListing;
